import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { planosAPI, formatters } from '../services/api';
import api from '../services/api';
import { getDeviceInfo } from '../utils/deviceInfo';
import {
  calculateAge,
  getTitularPriceByAge,
  getDependentPriceByAge,
  getPetPrice as getPetPriceShared,
  getExclusivePetPlanPrice,
} from '../utils/pricing';
import { isValidCPF, validateBirthDateAge } from '../utils/validators';

import Stepper from '../components/Stepper';
import PlanSelector from '../components/PlanSelector';
import PersonalDataForm from '../components/PersonalDataForm';
import DependentForm from '../components/DependentForm';
import AddressForm from '../components/AddressForm';
import PetsGrid from '../components/PetsGrid';
import PriceSummaryBar from '../components/PriceSummaryBar';
import SuccessModal from '../components/SuccessModal';
import ConfirmationModal from '../components/ConfirmationModal';
import SupportModal from '../components/SupportModal';

import {
  LuTicket,
  LuCalendarDays,
  LuPackage,
  LuClock,
  LuCheck,
  LuUser,
  LuUsers,
  LuDog,
  LuList,
  LuBuilding2,
  LuMapPin,
  LuLightbulb,
  LuFileText,
  LuBook,
  LuExternalLink,
  LuInfo,
  LuDollarSign,
  LuAlertCircle,
  LuAlertTriangle,
} from 'react-icons/lu';

const iconStyle = { display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' };

const TicketIcon = () => <LuTicket size={20} style={iconStyle} />;
const CalendarIcon = () => <LuCalendarDays size={20} style={iconStyle} />;
const PackageIcon = () => <LuPackage size={20} style={iconStyle} />;
const ClockIcon = () => <LuClock size={20} style={iconStyle} />;
const CheckIcon = () => <LuCheck size={16} style={iconStyle} />;
const UserIcon = () => <LuUser size={20} style={iconStyle} />;
const UsersIcon = () => <LuUsers size={20} style={iconStyle} />;
const PawIcon = () => <LuDog size={20} style={iconStyle} />;
const ListIcon = () => <LuList size={20} style={iconStyle} />;
const BuildingIcon = () => <LuBuilding2 size={20} style={iconStyle} />;
const MapPinIcon = () => <LuMapPin size={20} style={iconStyle} />;
const LightbulbIcon = () => <LuLightbulb size={20} style={iconStyle} />;

const PlansContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px 100px 20px; /* Espaço para a barra de preço fixa */
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    margin: 20px auto;
    padding: 0 15px 90px 15px;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  
  h1 {
    font-size: 32px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 10px;
    
    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
  
  p {
    font-size: 18px;
    color: ${props => props.theme.colors.grayMedium};
    
    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`;

const FormContainer = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.card};
  overflow: hidden;
`;

const FormContent = styled.div`
  padding: 40px;
  min-height: 400px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FormSection = styled.div`
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;


const InfoAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.theme.colors.lightBlue};
  padding: 14px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.primary};
  font-size: 0.875rem;
  line-height: 1.5;

  svg { flex-shrink: 0; }

  strong { font-weight: 600; }

  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 0.8125rem;
  }
`;

const InfoCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const InfoCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 15px;
  }
  
  h4 {
    color: ${props => props.theme.colors.primary};
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    @media (max-width: 768px) {
      font-size: 15px;
    }
  }
  
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  
  li {
    padding: 5px 0;
    color: ${props => props.theme.colors.grayDark};
    font-size: 14px;
    border-bottom: 1px solid #f1f5f9;
    
    &:last-child {
      border-bottom: none;
    }
    
    strong {
      color: ${props => props.theme.colors.primary};
    }
    
    @media (max-width: 768px) {
      font-size: 13px;
    }
  }
  
  p {
    margin-top: 15px;
    font-size: 12px;
    color: ${props => props.theme.colors.grayMedium};
    font-style: italic;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

const Button = styled.button`
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BackButton = styled(Button)`
  background: ${props => props.theme.colors.grayLight};
  color: ${props => props.theme.colors.grayDark};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.grayMedium};
    color: ${props => props.theme.colors.white};
  }
`;

const NextButton = styled(Button)`
  background: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.white};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.hoverPrimary};
  }
`;

const SubmitButton = styled(Button)`
  background: ${props => props.theme.colors.successGreen};
  color: ${props => props.theme.colors.white};

  &:hover:not(:disabled) {
    background: #059669;
  }
`;

const TermsSection = styled.div`
  background: ${props => props.theme.colors.grayLight};
  padding: 20px;
  border-radius: 8px;
  margin: 30px 0;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  
  input[type="checkbox"] {
    margin-top: 4px;
    transform: scale(1.2);
  }
`;

const TermsText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  
  a {
    color: ${props => props.theme.colors.secondary};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .required {
    color: ${props => props.theme.colors.error};
  }
`;

const SummarySection = styled.div`
  background: ${props => props.theme.colors.grayLight};
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
`;

const SummaryTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
`;

const SummaryItem = styled.div`
  padding: 15px;
  background: ${props => props.theme.colors.white};
  border-radius: 8px;
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 10px;
  }
  
  p {
    font-size: 14px;
    color: ${props => props.theme.colors.grayDark};
    margin: 5px 0;
    
    strong {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const ConfirmationFieldsSection = styled.div`
  background: ${props => props.theme.colors.grayLight};
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FieldInput = styled.input`
  padding: 12px;
  border: 2px solid ${props => props.theme.colors.grayLight};
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FieldSelect = styled.select`
  padding: 12px;
  border: 2px solid ${props => props.theme.colors.grayLight};
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FieldHint = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.grayMedium};
`;

const PlansWizard = () => {
  const location = useLocation();
  const [utmSource, setUtmSource] = useState(null);
  const [utmCampaign, setUtmCampaign] = useState(null);
  const [utmMedium, setUtmMedium] = useState(null);
  const [utmProvider, setUtmProvider] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [personalData, setPersonalData] = useState({});
  const [financialResponsibleData, setFinancialResponsibleData] = useState({});
  const [dependents, setDependents] = useState([]);
  const [addressData, setAddressData] = useState({});
  const [pets, setPets] = useState([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successContact, setSuccessContact] = useState({ type: 'email', value: '' });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isCpfInaptoModalOpen, setIsCpfInaptoModalOpen] = useState(false);
  const [isContratoPendenteModalOpen, setIsContratoPendenteModalOpen] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [cupomCode, setCupomCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [dataVencimento, setDataVencimento] = useState('10');
  const [receberFisico, setReceberFisico] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [valorFrete, setValorFrete] = useState(15.00); // Valor padrão, será atualizado pela API
  const [dependentBirthDateErrors, setDependentBirthDateErrors] = useState({});
  const [hasDependentBirthDateErrors, setHasDependentBirthDateErrors] = useState(false);
  const [checkoutSessionToken, setCheckoutSessionToken] = useState(null);

  // Sessão de checkout — necessária para validar CPF e contratar
  useEffect(() => {
    let mounted = true;

    const iniciarSessaoCheckout = async () => {
      try {
        const response = await planosAPI.createCheckoutSession();
        if (!mounted) return;
        setCheckoutSessionToken(response.data?.token || null);
      } catch (error) {
        console.error('Erro ao iniciar sessão de checkout:', error);
        if (mounted) {
          toast.error('Não foi possível iniciar a sessão. Recarregue a página.');
        }
      }
    };

    iniciarSessaoCheckout();

    return () => {
      mounted = false;
    };
  }, []);

  // Capturar UTM da URL do painel (/?utm_source=&utm_campaign=&utm_medium=&utm_provider=...)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const utm = params.get('utm_source') || null;
      const campaign = params.get('utm_campaign') || null;
      const medium = params.get('utm_medium') || null;
      const provider = params.get('utm_provider') || null;
      setUtmSource(utm);
      setUtmCampaign(campaign);
      setUtmMedium(medium);
      setUtmProvider(provider);
    } catch (error) {
      console.error('Erro ao ler parâmetros UTM da URL:', error);
      setUtmSource(null);
      setUtmCampaign(null);
      setUtmMedium(null);
      setUtmProvider(null);
    }
  }, [location.search]);

  // Buscar pricing e valor do frete
  useEffect(() => {
    let mounted = true;
    setPricingLoading(true);
    setPricingError(null);
    
    // Buscar valores de planos
    planosAPI
      .getValores()
      .then((res) => {
        const data = res.data?.data?.pricing || null;
        const freteFromAPI = res.data?.data?.valorFrete;
        if (mounted) {
          setPricing(data);
          setPricingError(null);
          if (freteFromAPI !== undefined) {
            setValorFrete(freteFromAPI);
          }
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar valores de planos:', err);
        if (mounted) {
          setPricingError('Não foi possível carregar os valores dos planos. Por favor, verifique sua conexão e tente novamente.');
        }
      })
      .finally(() => {
        if (mounted) {
          setPricingLoading(false);
        }
      });
    
    // Buscar valor do frete (fallback caso não venha em /valores)
    planosAPI
      .getValorFrete()
      .then((res) => {
        const frete = res.data?.data?.valorFrete;
        if (mounted && frete !== undefined) {
          setValorFrete(frete);
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar valor do frete:', err);
      });
    
    return () => {
      mounted = false;
    };
  }, []);

  // Calcular preço do pet
  const getPetPrice = (pet) => getPetPriceShared(pet, pricing, selectedPlan).price;

  // Obter nome do serviço PET
  const getPetServiceName = (pet) => getPetPriceShared(pet, pricing, selectedPlan).desc;

  // Calcular preços totais
  const calculatePriceBreakdown = useCallback(() => {
    if (!selectedPlan) return { items: [], total: 0 };
    
    const items = [];
    let total = 0;
    
    // Para planos exclusivos PET, titular não tem custo (valor 0)
    // Apenas os pets são cobrados
    const isExclusivePetPlan = selectedPlan.somentePET === true;
    
    // Preço do titular (apenas para planos não-PET)
    if (!isExclusivePetPlan && personalData.birthDate) {
      const age = calculateAge(personalData.birthDate);
      const price = getTitularPriceByAge(age, selectedPlan);
      if (price > 0) {
        items.push({
          label: `${selectedPlan.name} (Titular)`,
          age: age,
          price: price
        });
        total += price;
      }
    }
    
    // Preço dos dependentes (apenas para planos não-PET)
    if (!isExclusivePetPlan) {
      dependents.forEach((dep, idx) => {
        if (dep.birthDate) {
          const age = calculateAge(dep.birthDate);
          const price = getDependentPriceByAge(age, pricing, selectedPlan);
          if (price !== null) {
            items.push({
              label: `Dependente: ${dep.name || `Dependente ${idx + 1}`}`,
              age: age,
              price: price
            });
            total += price;
          }
        }
      });
    }
    
    // Preço dos pets
    pets.forEach((pet, idx) => {
      // Para planos exclusivos PET, calcular sem precisar de service
      // Para planos normais, só calcular se tiver service
      if (isExclusivePetPlan || pet.service) {
        const petPrice = isExclusivePetPlan
          ? getExclusivePetPlanPrice(pricing, selectedPlan)
          : getPetPrice(pet);
        
        const serviceName = isExclusivePetPlan 
          ? selectedPlan.name 
          : getPetServiceName(pet);
        
        items.push({
          label: `Pet: ${pet.name || `Pet ${idx + 1}`} - ${serviceName}`,
          age: pet.birthDate ? calculateAge(pet.birthDate) : null,
          price: petPrice
        });
        total += petPrice;
      }
    });
    
    return { items, total };
  }, [personalData, dependents, pets, selectedPlan, pricing]);

  // Função para formatar data para formato brasileiro
  const formatDateBR = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Aplicar cupom de desconto
  const handleApplyCoupon = async () => {
    if (!cupomCode.trim()) {
      setCouponError('Digite um código de cupom');
      toast.error('Digite um código de cupom');
      return;
    }

    const priceBreakdown = calculatePriceBreakdown();
    const totalPrice = priceBreakdown.total; // Valor da mensalidade SEM frete

    if (!totalPrice || totalPrice <= 0) {
      setCouponError('Erro: Valor total inválido');
      toast.error('Erro: Valor total inválido');
      return;
    }

    setCouponLoading(true);
    setCouponError(null);

    try {
      const valorTaxaAdesao = 0; // Taxa desabilitada
      
      // O cupom NÃO deve incidir sobre o frete, apenas sobre a mensalidade
      const payload = {
        codigo: cupomCode.toUpperCase(),
        valorTotal: totalPrice, // Apenas o valor da mensalidade (sem frete)
        valorTaxaAdesao: valorTaxaAdesao
      };

      const response = await api.post('/cupons/validar', payload);

      if (response.data && response.data.valido) {
        setAppliedCoupon(response.data);
        toast.success(`Cupom "${cupomCode}" aplicado com sucesso! Desconto de R$ ${response.data.valorDesconto.toFixed(2).replace('.', ',')} na 1ª mensalidade`);
        setCouponError(null);
      } else {
        const errorMsg = response.data?.message || response.data?.error || 'Cupom inválido';
        setCouponError(errorMsg);
        toast.error(errorMsg);
        setAppliedCoupon(null);
      }
    } catch (error) {
      console.error('Erro ao validar cupom:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Cupom inválido ou expirado';
      setCouponError(errorMsg);
      toast.error(errorMsg);
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // Remover cupom
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCupomCode('');
    setCouponError(null);
    toast.info('Cupom removido');
  };

  // URL base para documentos (sem duplicar /api)
  const getDocumentBaseURL = () => {
    // Se houver variável de ambiente, usar ela (sem barras duplicadas no final)
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
    }
    // Caso contrário, usar mesma URL de fallback de produção da API
    return 'https://vendasscc-backend.blueboxes.com.br/api';
  };

  // Scroll para o topo quando mudar de step
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);

  // Mesclar dados pessoais e endereço para o titular
  const holderData = {
    ...personalData,
    ...addressData
  };

  const hasFinancialResponsible = personalData.contratandoParaOutro === true && !(selectedPlan && selectedPlan.somentePET === true);

  const handlePlanSelect = (plan) => {
    // Verificar se está mudando de plano PET para normal ou vice-versa
    const wasExclusivePet = selectedPlan && selectedPlan.somentePET === true;
    const isExclusivePet = plan && plan.somentePET === true;
    
    // Se for plano exclusivo PET, remover dependentes humanos ANTES de atualizar o plano
    if (isExclusivePet) {
      if (dependents.length > 0) {
        setDependents([]);
        toast.info('Plano exclusivo PET selecionado. Dependentes humanos foram removidos.', {
          position: "top-center",
          autoClose: 4000
        });
      }
    }
    
    // Se estava em plano PET e mudou para plano normal, limpar pets sem serviço
    if (wasExclusivePet && !isExclusivePet) {
      if (pets.length > 0) {
        // Limpar pets que não têm o campo service (obrigatório em planos normais)
        const petsWithoutService = pets.filter(pet => !pet.service);
        if (petsWithoutService.length > 0) {
          setPets([]);
          toast.info('Mudou para plano normal. Você precisará adicionar os pets novamente e selecionar o tipo de serviço.', {
            position: "top-center",
            autoClose: 5000
          });
        }
      }
    }
    
    // Atualizar o plano selecionado APÓS fazer as limpezas
    setSelectedPlan(plan);
    
    // Em mobile, mostrar toast pedindo para descer a tela e clicar em próximo
    if (window.innerWidth <= 768 && currentStep === 1) {
      toast.info('Plano selecionado! Desça a tela e clique em "Próximo" para continuar.', {
        position: "top-center",
        autoClose: 5000,
        style: {
          fontSize: '14px'
        }
      });
    }
  };

  const handlePersonalDataChange = useCallback((data) => {
    setPersonalData(prev => {
      // Se deixou de ser "para outra pessoa" (manualmente ou forçado por plano somentePET),
      // limpa os dados do responsável financeiro para não carregá-los adiante.
      if (prev.contratandoParaOutro && !data.contratandoParaOutro) {
        setFinancialResponsibleData({});
      }
      return data;
    });
  }, []);

  const handleFinancialResponsibleChange = useCallback((data) => {
    setFinancialResponsibleData(data);
  }, []);

  const handleDependentsChange = useCallback((deps) => {
    setDependents(deps);
  }, []);

  const handleDependentValidationChange = useCallback((hasErrors, errors) => {
    setHasDependentBirthDateErrors(hasErrors);
    setDependentBirthDateErrors(errors);
  }, []);

  const handleAddressChange = useCallback((data) => {
    setAddressData(data);
  }, []);

  const handlePetsChange = useCallback((data) => {
    setPets(data);
  }, []);

  const handleCouponApply = useCallback((couponData) => {
    setAppliedCoupon(couponData);
  }, []);

  const handleStepClick = (stepId) => {
    if (stepId === currentStep) return;

    // Voltar para um passo anterior é sempre permitido, sem revalidar
    if (stepId < currentStep) {
      setCurrentStep(stepId);
      return;
    }

    // Avançar para um passo já concluído anteriormente: revalida o passo
    // atual, pois os dados podem ter mudado (ex: marcar "outra pessoa" e
    // tentar pular sem preencher o responsável financeiro)
    if (completedSteps.includes(stepId)) {
      if (!validateStep(currentStep)) {
        return;
      }
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }
      setCurrentStep(stepId);
    }
  };

  const validateFinancialResponsible = (fin) => {
    if (!fin.fullName || fin.fullName.trim().length < 3) {
      toast.error('Informe o nome completo do responsável financeiro');
      return false;
    }
    if (!isValidCPF(fin.cpf)) {
      toast.error('Informe um CPF válido do responsável financeiro');
      return false;
    }
    if (!fin.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fin.email)) {
      toast.error('Informe um e-mail válido do responsável financeiro');
      return false;
    }
    if (!fin.phone || fin.phone.replace(/\D/g, '').length < 10) {
      toast.error('Informe um telefone celular válido do responsável financeiro');
      return false;
    }
    if (!fin.cep || fin.cep.replace(/\D/g, '').length !== 8) {
      toast.error('Informe o CEP do responsável financeiro');
      return false;
    }
    if (!fin.street || fin.street.trim().length < 3) {
      toast.error('Informe o logradouro do responsável financeiro');
      return false;
    }
    if (!fin.number || fin.number.trim().length < 1) {
      toast.error('Informe o número do endereço do responsável financeiro');
      return false;
    }
    if (!fin.neighborhood || fin.neighborhood.trim().length < 2) {
      toast.error('Informe o bairro do responsável financeiro');
      return false;
    }
    if (!fin.city || fin.city.trim().length < 2) {
      toast.error('Informe a cidade do responsável financeiro');
      return false;
    }
    if (!fin.state) {
      toast.error('Selecione o estado do responsável financeiro');
      return false;
    }
    return true;
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: // Plano
        if (!selectedPlan) {
          toast.error('Por favor, selecione um plano para continuar');
          return false;
        }
        return true;

      case 2: // Dados Pessoais
        if (hasFinancialResponsible && !validateFinancialResponsible(financialResponsibleData)) {
          return false;
        }
        if (!personalData.fullName || personalData.fullName.trim().length < 3) {
          toast.error('Por favor, informe o nome completo');
          return false;
        }
        if (!isValidCPF(personalData.cpf)) {
          toast.error('Por favor, informe um CPF válido');
          return false;
        }
        if (hasFinancialResponsible) {
          const finCpf = (financialResponsibleData.cpf || '').replace(/\D/g, '');
          const titularCpf = personalData.cpf.replace(/\D/g, '');
          if (finCpf && titularCpf && finCpf === titularCpf) {
            toast.error('O CPF do responsável financeiro deve ser diferente do CPF do titular');
            return false;
          }
        }
        if (!personalData.birthDate) {
          toast.error('Por favor, informe a data de nascimento');
          return false;
        }
        // Titular: idade entre 18 e 120 anos
        const titularAgeCheck = validateBirthDateAge(personalData.birthDate, {
          minAge: 18,
          maxAge: 120,
        });
        if (!titularAgeCheck.valid) {
          toast.error(titularAgeCheck.error);
          return false;
        }
        // Idade precisa ter faixa de preço no plano selecionado
        if (selectedPlan && selectedPlan.somentePET !== true) {
          const hasTitularPricing =
            (selectedPlan.pricingTiers && selectedPlan.pricingTiers.length > 0) ||
            !!selectedPlan.pricing;
          if (hasTitularPricing) {
            const titularPrice = getTitularPriceByAge(titularAgeCheck.age, selectedPlan);
            if (titularPrice <= 0) {
              toast.error('A idade do titular está fora das faixas etárias deste plano');
              return false;
            }
          }
        }
        if (!personalData.gender) {
          toast.error('Por favor, selecione o sexo');
          return false;
        }
        if (!personalData.phone || personalData.phone.replace(/\D/g, '').length < 10) {
          toast.error('Por favor, informe um telefone válido');
          return false;
        }
        // Validar email OU WhatsApp
        const emailFilled = !!(personalData.email && personalData.email.trim());
        const hasValidEmail = emailFilled && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalData.email);
        const hasWhatsApp = personalData.isWhatsApp;
        if (emailFilled && !hasValidEmail) {
          toast.error('Por favor, informe um e-mail válido ou deixe o campo em branco');
          return false;
        }
        if (!hasValidEmail && !hasWhatsApp) {
          toast.error('Por favor, informe um e-mail válido ou marque o WhatsApp');
          return false;
        }
        return true;

      case 3: // Dependentes (humanos e pets)
        // Verificar se há erros de data de nascimento nos dependentes
        if (hasDependentBirthDateErrors) {
          const errorMessages = Object.values(dependentBirthDateErrors);
          if (errorMessages.length > 0) {
            toast.error(errorMessages[0]);
            return false;
          }
        }
        
        // Para plano exclusivo PET, deve ter pelo menos um pet
        if (selectedPlan?.somentePET === true && pets.length === 0) {
          toast.error('Para planos exclusivos PET, você deve adicionar pelo menos um animal de estimação');
          return false;
        }
        
        // Validar dependentes humanos se houver
        if (dependents.length > 0) {
          for (let i = 0; i < dependents.length; i++) {
            const dep = dependents[i];
            if (!dep.name || !dep.birthDate || !dep.gender || !dep.relationship) {
              toast.error(`Dependente ${i + 1}: Preencha todos os campos obrigatórios (Nome, Data de Nascimento, Sexo e Parentesco)`);
              return false;
            }
            const depAgeCheck = validateBirthDateAge(dep.birthDate, {
              minAge: 0,
              maxAge: 120,
            });
            if (!depAgeCheck.valid) {
              toast.error(`Dependente ${i + 1}: ${depAgeCheck.error}`);
              return false;
            }
            // Idade precisa ter faixa de preço no plano
            if (selectedPlan && selectedPlan.somentePET !== true && pricing) {
              const depTiers = pricing.dependentes?.[String(selectedPlan.idPlano)]?.tiers;
              if (Array.isArray(depTiers) && depTiers.length > 0) {
                const depPrice = getDependentPriceByAge(depAgeCheck.age, pricing, selectedPlan);
                if (depPrice === null) {
                  toast.error(`Dependente ${i + 1}: A idade está fora das faixas etárias deste plano`);
                  return false;
                }
              }
            }
            // CPF é opcional, mas se informado precisa ser válido
            const depCpfDigits = (dep.cpf || '').replace(/\D/g, '');
            if (depCpfDigits && !isValidCPF(dep.cpf)) {
              toast.error(`Dependente ${i + 1}: Informe um CPF válido ou deixe o campo em branco`);
              return false;
            }
          }
        }
        
        // Validar pets se houver
        if (pets.length > 0) {
          for (let i = 0; i < pets.length; i++) {
            const pet = pets[i];
            
            // Campos básicos sempre obrigatórios (idade 0 é válida)
            const petAgeRaw = pet.age;
            if (
              !pet.name ||
              !pet.group ||
              petAgeRaw === '' ||
              petAgeRaw == null ||
              !pet.weight ||
              !pet.breed ||
              !pet.sex
            ) {
              toast.error(`Pet ${i + 1}: Preencha todos os campos obrigatórios`);
              return false;
            }

            const petAge = parseInt(petAgeRaw, 10);
            if (Number.isNaN(petAge) || petAge < 0 || petAge > 30) {
              toast.error(`Pet ${i + 1}: Informe uma idade válida (0 a 30 anos)`);
              return false;
            }
            
            // Campo 'service' obrigatório apenas quando o plano NÃO é exclusivo para pets
            if (selectedPlan && !selectedPlan.somentePET && !pet.service) {
              toast.error(`Pet ${i + 1}: Selecione o tipo de serviço`);
              return false;
            }
          }
        }
        
        return true;

      case 4: // Endereço
        if (!addressData.cep || addressData.cep.replace(/\D/g, '').length !== 8) {
          toast.error('Por favor, informe um CEP válido');
          return false;
        }
        if (!addressData.street || addressData.street.trim().length < 3) {
          toast.error('Por favor, informe o logradouro');
          return false;
        }
        if (!addressData.number || addressData.number.trim().length < 1) {
          toast.error('Por favor, informe o número');
          return false;
        }
        if (!addressData.neighborhood || addressData.neighborhood.trim().length < 2) {
          toast.error('Por favor, informe o bairro');
          return false;
        }
        if (!addressData.city || addressData.city.trim().length < 2) {
          toast.error('Por favor, informe a cidade');
          return false;
        }
        if (!addressData.state) {
          toast.error('Por favor, selecione o estado');
          return false;
        }
        return true;

      case 5:
        if (!acceptTerms) {
          toast.error('Você deve aceitar os termos para prosseguir');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Marcar passo como completo
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = () => {
    if (!validateStep(5)) {
      return;
    }
    
    // Mostrar modal de confirmação antes de processar
    setIsConfirmationModalOpen(true);
  };

  const ensureCheckoutSession = useCallback(async () => {
    if (checkoutSessionToken) return checkoutSessionToken;

    const response = await planosAPI.createCheckoutSession();
    const token = response.data?.token || null;
    if (token) setCheckoutSessionToken(token);
    return token;
  }, [checkoutSessionToken]);

  const resetWizardState = useCallback(() => {
    setCurrentStep(1);
    setCompletedSteps([]);
    setSelectedPlan(null);
    setPersonalData({});
    setFinancialResponsibleData({});
    setDependents([]);
    setAddressData({});
    setPets([]);
    setAcceptTerms(false);
    setAppliedCoupon(null);
    setCupomCode('');
    setCouponError(null);
    setDataVencimento('10');
    setReceberFisico(false);
    setDependentBirthDateErrors({});
    setHasDependentBirthDateErrors(false);
    setIsConfirmationModalOpen(false);
  }, []);

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);

    try {
      let sessionToken = await ensureCheckoutSession();
      if (!sessionToken) {
        toast.error('Sessão expirada. Recarregue a página e tente novamente.');
        return;
      }

      const cpfLimpo = (personalData.cpf || '').replace(/\D/g, '');

      let validacaoResponse;
      try {
        validacaoResponse = await planosAPI.validaCpf(cpfLimpo, sessionToken);
      } catch (error) {
        if (error?.response?.status === 401) {
          const refreshResponse = await planosAPI.createCheckoutSession();
          sessionToken = refreshResponse.data?.token || null;
          if (sessionToken) setCheckoutSessionToken(sessionToken);
          if (!sessionToken) throw error;
          validacaoResponse = await planosAPI.validaCpf(cpfLimpo, sessionToken);
        } else {
          throw error;
        }
      }

      const resultado = validacaoResponse.data;

      if (resultado?.status === 'contrato_pendente') {
        setIsConfirmationModalOpen(false);
        setIsContratoPendenteModalOpen(true);
        return;
      }

      if (resultado?.status === 'inapto') {
        setIsConfirmationModalOpen(false);
        setIsCpfInaptoModalOpen(true);
        return;
      }

      if (resultado?.status !== 'apto') {
        toast.error(resultado?.mensagem || 'Não foi possível validar o CPF. Tente novamente.');
        return;
      }

      setIsConfirmationModalOpen(false);

      // Coletar informações do dispositivo
      const deviceInfo = getDeviceInfo();

      // Contato do titular (confirmação do link)
      const contactType = personalData.isWhatsApp ? 'whatsapp' : 'email';
      const contactValue = personalData.isWhatsApp
        ? (personalData.phone || '')
        : (personalData.email || '');
      
      const payload = {
        selectedPlan: selectedPlan ? { 
          idPlano: selectedPlan.idPlano,
          key: selectedPlan.key, // Manter por compatibilidade
          name: selectedPlan.name, 
          pricingTiers: selectedPlan.pricingTiers, 
          pricing: selectedPlan.pricing,
          somentePET: selectedPlan.somentePET || false
        } : null,
        holder: holderData,
        financialResponsible: hasFinancialResponsible ? financialResponsibleData : null,
        dependents,
        pets,
        acceptTerms,
        timestamp: new Date().toISOString(),
        cupomCode: cupomCode.trim() || null,
        dataVencimento: dataVencimento || '10',
        receberFisico: receberFisico,
        contactType,
        contactValue,
        utm_source: utmSource || null,
        utm_campaign: utmCampaign || null,
        utm_medium: utmMedium || null,
        utm_provider: utmProvider || null,
        device: deviceInfo, // Informações do dispositivo/navegador
        cupom: appliedCoupon ? {
          codigo: appliedCoupon.cupom.codigo,
          vendedorId: appliedCoupon.cupom.vendedorId,
          vendedorNome: appliedCoupon.cupom.vendedorNome,
          beneficio: appliedCoupon.cupom.beneficio,
          valorOriginal: appliedCoupon.valorOriginal,
          valorDesconto: appliedCoupon.valorDesconto,
          valorFinal: appliedCoupon.valorFinal
        } : null
      };

      await planosAPI.submitContratacao(payload, sessionToken);

      // Preserva contato do titular para o modal antes de limpar o formulário
      setSuccessContact({
        type: contactType,
        value: contactValue
      });
      setIsSuccessModalOpen(true);
      resetWizardState();
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      const msg = error?.response?.data?.message || 'Erro ao enviar formulário. Tente novamente.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setIsConfirmationModalOpen(false);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormSection>
            <SectionTitle>Escolha seu Plano</SectionTitle>
            <InfoAlert>
              <LightbulbIcon /> Escolha um plano e toque em "Próximo" para continuar.
            </InfoAlert>
            <PlanSelector 
              onPlanSelect={handlePlanSelect}
              selectedPlan={selectedPlan}
            />
          </FormSection>
        );

      case 2:
        return (
          <FormSection>
            <PersonalDataForm
              onDataChange={handlePersonalDataChange}
              data={personalData}
              financialResponsibleData={financialResponsibleData}
              onFinancialResponsibleChange={handleFinancialResponsibleChange}
              selectedPlan={selectedPlan}
            />
          </FormSection>
        );

      case 3:
        return (
          <FormSection>
            {/* Dependentes - Apenas se o plano não for exclusivo para PET */}
            {selectedPlan && !selectedPlan.somentePET && (
              <>
                <SectionTitle>Dependentes (Opcional)</SectionTitle>
                <InfoAlert>
                  <LightbulbIcon /> Os valores dos dependentes são fixos por faixa etária, independentemente do plano escolhido.
                </InfoAlert>
                <DependentForm 
                  onDependentsChange={handleDependentsChange}
                  dependents={dependents}
                  selectedPlan={selectedPlan}
                  onValidationChange={handleDependentValidationChange}
                />
              </>
            )}
            
            {/* Pets - Obrigatório para planos exclusivos PET, opcional para demais planos */}
            <SectionTitle style={{ marginTop: '40px' }}>
              Pets {selectedPlan?.somentePET ? '(Obrigatório)' : '(Opcional)'}
            </SectionTitle>
            <InfoAlert>
              <PawIcon /> {
                selectedPlan?.somentePET
                  ? 'Adicione pelo menos um pet para continuar com o plano PET.'
                  : 'A inclusão de pets é opcional e os valores independem do plano escolhido.'
              }
            </InfoAlert>
            <PetsGrid 
              pets={pets}
              onPetsChange={handlePetsChange}
              selectedPlan={selectedPlan}
            />
          </FormSection>
        );

      case 4:
        return (
          <FormSection>
            <AddressForm 
              onDataChange={handleAddressChange}
              data={addressData}
            />
          </FormSection>
        );

      case 5:
        return (
          <FormSection>
            <SectionTitle>Confirmar Contratação</SectionTitle>
            
            <SummarySection style={{ marginBottom: '15px' }}>
              <SummaryTitle>Resumo da Contratação</SummaryTitle>
              
              <SummaryItem>
                <h4><ListIcon /> Plano Selecionado</h4>
                <p><strong>Plano:</strong> {selectedPlan?.name}</p>
              </SummaryItem>
              
              <SummaryItem>
                <h4><UserIcon /> Titular</h4>
                <p><strong>Nome:</strong> {personalData.fullName}</p>
                <p><strong>CPF:</strong> {personalData.cpf}</p>
                <p><strong>Data de Nascimento:</strong> {formatDateBR(personalData.birthDate)} {personalData.birthDate && `(${calculateAge(personalData.birthDate)} ${calculateAge(personalData.birthDate) === 1 ? 'ano' : 'anos'})`}</p>
              </SummaryItem>
              
              <SummaryItem>
                <h4><MapPinIcon /> Endereço do Titular</h4>
                <p>{addressData.street}, {addressData.number}</p>
                {addressData.complement && <p>{addressData.complement}</p>}
                <p>{addressData.neighborhood} - {addressData.city}/{addressData.state}</p>
                <p><strong>CEP:</strong> {addressData.cep}</p>
              </SummaryItem>

              {hasFinancialResponsible && (
                <SummaryItem>
                  <h4><BuildingIcon /> Contratante</h4>
                  <p><strong>Nome:</strong> {financialResponsibleData.fullName}</p>
                  <p><strong>CPF:</strong> {financialResponsibleData.cpf}</p>
                  <p><strong>E-mail:</strong> {financialResponsibleData.email}</p>
                  <p><strong>Celular:</strong> {financialResponsibleData.phone}</p>
                  {financialResponsibleData.phoneResidential && (
                    <p><strong>Telefone Residencial:</strong> {financialResponsibleData.phoneResidential}</p>
                  )}
                  <p>{financialResponsibleData.street}, {financialResponsibleData.number}</p>
                  {financialResponsibleData.complement && <p>{financialResponsibleData.complement}</p>}
                  <p>{financialResponsibleData.neighborhood} - {financialResponsibleData.city}/{financialResponsibleData.state}</p>
                  <p><strong>CEP:</strong> {financialResponsibleData.cep}</p>
                  {financialResponsibleData.addressReference && (
                    <p><strong>Referência:</strong> {financialResponsibleData.addressReference}</p>
                  )}
                </SummaryItem>
              )}
              
              {dependents.length > 0 && (
                <SummaryItem>
                  <h4><UsersIcon /> Dependentes</h4>
                  {(() => {
                    // Mapeamento de valores numéricos para labels de parentesco (baseado no DependentForm.js)
                    const relationshipLabels = {
                      '1': 'FILHO',
                      '2': 'FILHA',
                      '3': 'PAI',
                      '4': 'MÃE',
                      '5': 'IRMÃO',
                      '6': 'IRMÃ',
                      '7': 'AVÔ',
                      '8': 'AVÓ',
                      '9': 'CUNHADO',
                      '10': 'CUNHADA',
                      '11': 'TIO',
                      '12': 'TIA',
                      '13': 'ESPOSO',
                      '14': 'ESPOSA',
                      '15': 'SOBRINHO',
                      '16': 'SOBRINHA',
                      '17': 'PRIMO',
                      '18': 'PRIMA',
                      '19': 'NETA',
                      '20': 'NETO',
                      '21': 'NORA',
                      '22': 'GENRO',
                      '23': 'SOGRA',
                      '24': 'SOGRO',
                      '25': 'COMPANHEIRA',
                      '26': 'COMPANHEIRO',
                      '27': 'OUTROS',
                      '28': 'ENTEADO(A)',
                      '29': 'BISNETO(A)',
                      '30': 'PADRASTO',
                      '31': 'MADRASTA',
                      '33': 'AFILHADO(A)',
                      '35': 'TATARANETO(A)',
                      '37': 'PADRINHO',
                      '40': 'BISAVÓ',
                      '41': 'BISAVÔ'
                    };
                    
                    return dependents.map((dep, idx) => {
                      const depAge = dep.birthDate ? calculateAge(dep.birthDate) : null;
                      const relationshipLabel = relationshipLabels[dep.relationship] || dep.relationship;
                      return (
                        <p key={dep.id || idx}>
                          Dependente {idx + 1}: {dep.name} ({depAge ? `${depAge} anos - ` : ''}{relationshipLabel})
                        </p>
                      );
                    });
                  })()}
                </SummaryItem>
              )}
              
              {pets.length > 0 && (
                <SummaryItem>
                  <h4><PawIcon /> Pets</h4>
                  {pets.map((pet, idx) => {
                    const age = pet.birthDate ? calculateAge(pet.birthDate) : null;
                    return (
                      <p key={idx}>
                        {idx + 1}. {pet.name} ({pet.group}){age ? `, ${age} ${age === 1 ? 'ano' : 'anos'}` : ''}
                      </p>
                    );
                  })}
                </SummaryItem>
              )}
            </SummarySection>

            {/* Campos de Confirmação */}
            <ConfirmationFieldsSection>
              <SummaryTitle>Informações Adicionais</SummaryTitle>
              <FieldRow>
                <FieldGroup>
                  <FieldLabel htmlFor="cupom-code">
                    <TicketIcon /> Cupom de Desconto
                  </FieldLabel>
                  
                  {!appliedCoupon ? (
                    <>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <FieldInput
                          id="cupom-code"
                          type="text"
                          placeholder="Digite o código do cupom"
                          value={cupomCode}
                          onChange={(e) => setCupomCode(e.target.value.toUpperCase())}
                          onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                          maxLength={20}
                          disabled={couponLoading}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !cupomCode.trim()}
                          style={{
                            padding: '12px 24px',
                            background: couponLoading || !cupomCode.trim() ? '#94a3b8' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: couponLoading || !cupomCode.trim() ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            if (!couponLoading && cupomCode.trim()) {
                              e.currentTarget.style.background = '#2563eb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!couponLoading && cupomCode.trim()) {
                              e.currentTarget.style.background = '#3b82f6';
                            }
                          }}
                        >
                          {couponLoading ? 'Validando...' : 'Aplicar'}
                        </button>
                      </div>
                      {couponError && (
                        <div style={{ fontSize: '13px', color: '#ef4444', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <LuAlertCircle size={16} />
                          {couponError}
                        </div>
                      )}
                      <FieldHint>
                        Digite o código do cupom e clique em "Aplicar"
                      </FieldHint>
                    </>
                  ) : (
                    <div style={{ background: '#f0fdf4', border: '2px solid #10b981', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#047857', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <LuCheck size={16} /> Cupom: {appliedCoupon.cupom.codigo}
                          </div>
                          <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>
                            Desconto aplicado na 1ª mensalidade
                          </div>
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#047857' }}>
                          - R$ {appliedCoupon.valorDesconto.toFixed(2).replace('.', ',')}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveCoupon}
                        style={{
                          padding: '6px 12px',
                          background: 'white',
                          color: '#dc2626',
                          border: '1px solid #dc2626',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          width: '100%'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#dc2626';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = '#dc2626';
                        }}
                      >
                        Remover Cupom
                      </button>
                    </div>
                  )}
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel htmlFor="data-vencimento">
                    <CalendarIcon /> Dia do Vencimento
                  </FieldLabel>
                  <FieldSelect
                    id="data-vencimento"
                    value={dataVencimento}
                    onChange={(e) => setDataVencimento(e.target.value)}
                  >
                    <option value="">Selecione o dia</option>
                    {Array.from({ length: 27 }, (_, i) => i + 1).map(dia => (
                      <option key={dia} value={dia}>
                        Dia {dia.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </FieldSelect>
                  <FieldHint>
                    Escolha o melhor dia para o vencimento da sua mensalidade
                  </FieldHint>
                </FieldGroup>
              </FieldRow>

              <FieldGroup style={{ marginTop: '20px' }}>
                <FieldLabel htmlFor="receber-fisico">
                  <PackageIcon /> Entrega de Carnê Físico
                </FieldLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '2px solid #e2e8f0' }}>
                  <input
                    type="checkbox"
                    id="receber-fisico"
                    checked={receberFisico}
                    onChange={(e) => setReceberFisico(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="receber-fisico" style={{ cursor: 'pointer', fontSize: '14px', color: '#334155', userSelect: 'none' }}>
                    <strong>Desejo receber o carnê físico</strong>
                    <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                      {receberFisico
                        ? (valorFrete > 0
                          ? <><CheckIcon /> Será cobrado {formatters.formatCurrency(valorFrete)} de frete</>
                          : <><CheckIcon /> Frete grátis</>)
                        : (valorFrete > 0
                          ? 'Carnê digital sem custo adicional'
                          : 'Carnê físico e digital sem custo adicional')}
                    </span>
                  </label>
                </div>
              </FieldGroup>
            </ConfirmationFieldsSection>

            {/* Resumo de Valores */}
            {(() => {
              const priceBreakdown = calculatePriceBreakdown();
              const subtotalMensal = priceBreakdown.total; // Valor mensal sem desconto
              const desconto = appliedCoupon ? appliedCoupon.valorDesconto : 0;
              const frete = receberFisico ? valorFrete : 0;
              
              // Primeira mensalidade: subtotal + frete - desconto (desconto não incide sobre frete)
              const primeiraMensalidade = subtotalMensal + frete - desconto;
              // Demais mensalidades: apenas o subtotal (sem desconto e sem frete)
              const demaisMensalidades = subtotalMensal;
              
              return (
                <SummarySection style={{ background: '#ffffff', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                  <SummaryTitle style={{ marginBottom: '15px' }}>
                    <LuDollarSign size={20} style={iconStyle} />
                    Resumo Financeiro
                  </SummaryTitle>
                  
                  <SummaryItem style={{ background: 'transparent', padding: 0, marginBottom: 0 }}>
                    {/* Itens do breakdown */}
                    {priceBreakdown.items.map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '15px', color: '#64748b' }}>
                          {item.label}
                          {item.age && ` (${item.age} ${item.age === 1 ? 'ano' : 'anos'})`}
                        </span>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                          {idx === 0 && selectedPlan?.key !== 'pet' ? '' : '+ '}R$ {item.price.toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                    ))}
                    
                    {/* Total Mensal (sem desconto) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e3a8a' }}>Mensalidade</span>
                      <span style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a8a' }}>
                        R$ {subtotalMensal.toFixed(2).replace('.', ',')}
                      </span>
                    </div>
                    
                    {/* Detalhamento da 1ª Mensalidade */}
                    <div style={{ marginTop: '20px', padding: '16px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fbbf24' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '12px' }}>
                        Composição da 1ª Mensalidade:
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#78350f', marginBottom: '6px' }}>
                        <span>Valor da Mensalidade</span>
                        <span>R$ {subtotalMensal.toFixed(2).replace('.', ',')}</span>
                      </div>
                      
                      {frete > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#78350f', marginBottom: '6px' }}>
                          <span>+ Frete do Carnê Físico</span>
                          <span>{formatters.formatCurrency(valorFrete)}</span>
                        </div>
                      )}
                      
                      {desconto > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#047857', fontWeight: '600', marginBottom: '6px' }}>
                          <span>- Desconto do Cupom ({appliedCoupon.cupom.codigo})</span>
                          <span>R$ {desconto.toFixed(2).replace('.', ',')}</span>
                        </div>
                      )}
                      
                      <div style={{ borderTop: '1px solid #fbbf24', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#92400e' }}>
                        <span>Total da 1ª Mensalidade</span>
                        <span>R$ {primeiraMensalidade.toFixed(2).replace('.', ',')}</span>
                      </div>
                    </div>
                    
                    {/* Aviso sobre demais mensalidades */}
                    {(desconto > 0 || frete > 0) && (
                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '12px', padding: '8px', background: '#f1f5f9', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                        <LuInfo size={16} style={{ ...iconStyle, color: '#475569' }} />
                        <strong>Atenção:</strong> As demais mensalidades serão de <strong>R$ {demaisMensalidades.toFixed(2).replace('.', ',')}</strong>
                        {desconto > 0 && ' (sem o desconto do cupom)'}
                        {frete > 0 && desconto > 0 && ' e '}
                        {frete > 0 && ' (sem o frete)'}
                      </div>
                    )}
                  </SummaryItem>
                </SummarySection>
              );
            })()}

            {/* Informações Importantes */}
            <SectionTitle>Informações Importantes</SectionTitle>
            <InfoCardsGrid>
              <InfoCard>
                <h4><ClockIcon /> Período de Carência</h4>
                <ul>
                  <li><strong>Até 59 anos:</strong> 60 dias (2 meses)</li>
                  <li><strong>60 anos ou mais:</strong> 120 dias (4 meses)</li>
                  <li><strong>Plano Pet:</strong> 90 dias (0-7 anos) / 180 dias (8+ anos)</li>
                </ul>
                <p><em>Carência contada a partir da data do primeiro pagamento</em></p>
              </InfoCard>
              
              <InfoCard>
                <h4><BuildingIcon /> Cemitérios Cobertos</h4>
                <ul>
                  <li>São João Batista (Botafogo)</li>
                  <li>Inhaúma, Cacúia, Irajá</li>
                  <li>Murundu, Campo Grande</li>
                  <li>Ricardo de Albuquerque</li>
                  <li>Pechincha, São Francisco Xavier</li>
                  <li>E outros cemitérios municipais do RJ</li>
                </ul>
              </InfoCard>
              
              {/* DESABILITADO - Taxa de adesão */}
              {/* <InfoCard>
                <h4>Taxa de Adesão</h4>
                <ul>
                  <li><strong>Planos Humanos:</strong> Mesmo valor da primeira mensalidade</li>
                  <li><strong>Plano Pet:</strong> R$ 100,00</li>
                  <li><strong>Plano Igreja:</strong> Mesmo valor da primeira mensalidade</li>
                </ul>
              </InfoCard> */}
            </InfoCardsGrid>

            {/* Termos e Contrato */}
            <TermsSection>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#1e40af', marginBottom: '16px', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                  <LuFileText size={20} style={iconStyle} />
                  Termos e Contratos
                </h3>
                
                <div style={{ background: '#f0f9ff', padding: '16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #bae6fd' }}>
                  <p style={{ fontSize: '14px', color: '#0c4a6e', marginBottom: '12px', lineHeight: '1.6' }}>
                    <strong>Leia os documentos abaixo antes de aceitar:</strong>
                  </p>
                  
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {/* Documentos para planos humanos (não específicos para pet) */}
                    {(!selectedPlan || selectedPlan.category !== 'pet') && (
                      <>
                        <a 
                          href={`${getDocumentBaseURL()}/documentos/contrato.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            padding: '12px 14px', 
                            background: 'white', 
                            border: '1px solid #3b82f6', 
                            borderRadius: '6px', 
                            color: '#1e40af', 
                            textDecoration: 'none', 
                            fontSize: '14px', 
                            fontWeight: '500',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#1e40af'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                        >
                          <LuFileText size={18} />
                          <span style={{ flex: 1 }}>Contrato de Adesão</span>
                          <LuExternalLink size={16} />
                        </a>
                        
                        <a 
                          href={`${getDocumentBaseURL()}/documentos/direito-associados.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            padding: '12px 14px', 
                            background: 'white', 
                            border: '1px solid #3b82f6', 
                            borderRadius: '6px', 
                            color: '#1e40af', 
                            textDecoration: 'none', 
                            fontSize: '14px', 
                            fontWeight: '500',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#1e40af'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                        >
                          <LuBook size={18} />
                          <span style={{ flex: 1 }}>Direitos do Associado</span>
                          <LuExternalLink size={16} />
                        </a>
                      </>
                    )}
                    
                    {/* Documentos para planos com pets */}
                    {pets && pets.length > 0 && (
                      <>
                        <a 
                          href={`${getDocumentBaseURL()}/documentos/contrato-pet.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            padding: '12px 14px', 
                            background: 'white', 
                            border: '1px solid #3b82f6', 
                            borderRadius: '6px', 
                            color: '#1e40af', 
                            textDecoration: 'none', 
                            fontSize: '14px', 
                            fontWeight: '500',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#1e40af'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                        >
                          <LuFileText size={18} />
                          <span style={{ flex: 1 }}>Contrato Pet</span>
                          <LuExternalLink size={16} />
                        </a>
                        
                        <a 
                          href={`${getDocumentBaseURL()}/documentos/direito-associados-pet.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            padding: '12px 14px', 
                            background: 'white', 
                            border: '1px solid #3b82f6', 
                            borderRadius: '6px', 
                            color: '#1e40af', 
                            textDecoration: 'none', 
                            fontSize: '14px', 
                            fontWeight: '500',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#1e40af'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                        >
                          <LuBook size={18} />
                          <span style={{ flex: 1 }}>Direitos do Associado Pet</span>
                          <LuExternalLink size={16} />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px' }}>
                <CheckboxWrapper>
                  <input 
                    type="checkbox" 
                    id="acceptTerms" 
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    required 
                  />
                  <TermsText>
                    <label htmlFor="acceptTerms">
                      Li e aceito os termos, condições e contratos apresentados acima. 
                      Declaro ter compreendido todas as cláusulas e condições. <span className="required">*</span>
                    </label>
                  </TermsText>
                </CheckboxWrapper>
              </div>
            </TermsSection>
          </FormSection>
        );

      default:
        return null;
    }
  };

  return (
    <PlansContainer>
      <PageHeader>
        <h1>Plano Assistencial Santa Casa Copacabana</h1>
        <p>Assistência para você, sua família e seus pets.</p>
      </PageHeader>

      {/* Mensagem de erro da API */}
      {pricingError && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderLeft: '4px solid #dc2626',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'fadeIn 0.3s ease'
        }}>
          <LuAlertTriangle size={24} color="#dc2626" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '4px', fontSize: '14px' }}>
              Serviço temporariamente indisponível
            </div>
            <div style={{ color: '#991b1b', fontSize: '13px', lineHeight: 1.5 }}>
              {pricingError}
            </div>
          </div>
        </div>
      )}
      
      <FormContainer>
        <Stepper 
          currentStep={currentStep}
          onStepClick={handleStepClick}
          completedSteps={completedSteps}
        />

        <FormContent>
          {renderStepContent()}

          <ButtonContainer>
            <BackButton 
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              Voltar
            </BackButton>

            {currentStep < 5 ? (
              <NextButton 
                onClick={handleNext}
                disabled={currentStep === 3 && hasDependentBirthDateErrors}
              >
                Próximo
              </NextButton>
            ) : (
              <SubmitButton 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processando...' : 'Contratar Plano'}
              </SubmitButton>
            )}
          </ButtonContainer>
        </FormContent>
      </FormContainer>

      <SuccessModal 
        open={isSuccessModalOpen}
        contactType={successContact.type}
        contactValue={successContact.value}
        onBeforeRedirect={resetWizardState}
      />
      
      <ConfirmationModal 
        open={isConfirmationModalOpen}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
        contactType={personalData.isWhatsApp ? 'whatsapp' : 'email'}
        contactValue={personalData.isWhatsApp ? personalData.phone : personalData.email}
        isLoading={isSubmitting}
      />

      <SupportModal
        variant="inapto"
        open={isCpfInaptoModalOpen}
        onClose={() => setIsCpfInaptoModalOpen(false)}
      />

      <SupportModal
        variant="pendente"
        open={isContratoPendenteModalOpen}
        onClose={() => setIsContratoPendenteModalOpen(false)}
      />
      
      {/* Barra de resumo de preço fixa */}
      {selectedPlan && currentStep > 1 && currentStep < 5 && (
        <PriceSummaryBar
          selectedPlan={selectedPlan}
          holderData={holderData}
          dependents={dependents}
          pets={pets}
          appliedCoupon={appliedCoupon}
          onCouponApply={handleCouponApply}
          receberFisico={receberFisico}
          onReceberFisicoChange={setReceberFisico}
          valorFrete={valorFrete}
        />
      )}
    </PlansContainer>
  );
};

export default PlansWizard;

